import { IConfirmationModalProps } from '../../../view/components/ConfirmationModal';
import { QuotaResourceKey } from '../../../view/components/Billing/QuotaLimitModal';
import { UpgradePromptFeatureKey } from '../../../view/components/Billing/UpgradePromptModal';
import { IBirth } from '@clickersphere/core/entities/IBirth';
import { IContractTemplate } from '@clickersphere/core/entities/IContractTemplate';
import { IContractor } from '@clickersphere/core/entities/IContractor';
import { IDowngradeBlockedDetails } from '@clickersphere/core/entities/ISubscription';
import { IEvent } from '@clickersphere/core/entities/IEvent';
import { IEventChecklist } from '@clickersphere/core/entities/IEventChecklist';
import { ILead } from '@clickersphere/core/entities/ILead';
import { ILeadSale } from '@clickersphere/core/entities/ILeadSale';
import { IMessageTemplate } from '@clickersphere/core/entities/IMessageTemplate';
import { IService } from '@clickersphere/core/entities/IService';
import { IServicePackage } from '@clickersphere/core/entities/IServicePackage';
import { ITodo } from '@clickersphere/core/entities/ITodo';
import { ModalTypeEnum } from '../../enums/ModalTypeEnum';
import { StoreSlice } from '../Store';

interface IConflictPackage {
  id: string;
  name: string;
}

interface IQuotaLimitPayload {
  current: number;
  limit: number;
  resourceKey: QuotaResourceKey;
}

interface IUpgradePromptPayload {
  featureKey: UpgradePromptFeatureKey;
}

interface IPlanUpgradePayload {
  planId: string;
}

interface IPlanDowngradeBlockedPayload {
  details: IDowngradeBlockedDetails;
}

interface ICancelSubscriptionPayload {
  cycleEndDate: Date | null;
}

interface ICollectDocumentPayload {
  onDocumentSaved: () => void;
}

type ModalStore = {
  openModal: ModalTypeEnum | null;
  lead?: ILead;
  event?: IEvent;
  todo?: ITodo;
  leadId?: string;
  eventStatusId?: string;
  todoLeadId?: string;
  todoEventId?: string;
  isConfirmationModalOpen: boolean;
  confirmationModalProps?: IConfirmationModalProps;
  service?: IService;
  servicePackage?: IServicePackage;
  deleteServiceConflict?: { service: IService; packages: IConflictPackage[] };
  registerSaleLeadId?: string;
  quotaLimit?: IQuotaLimitPayload;
  upgradePrompt?: IUpgradePromptPayload;
  planUpgrade?: IPlanUpgradePayload;
  planDowngradeBlocked?: IPlanDowngradeBlockedPayload;
  cancelSubscription?: ICancelSubscriptionPayload;
  selectedPlanIdForChange?: string;
  collectDocument?: ICollectDocumentPayload;
  contractor?: IContractor;
  contractTemplate?: IContractTemplate;
  issueContractLeadId?: string;
  sendForSignature?: ISendForSignaturePayload;
  messageTemplate?: IMessageTemplate;
  messageComposerLead?: ILead;
  eventChecklist?: IEventChecklist;
  deactivateLead?: ILead;
  eventDraft?: IEventDraft;
  saleDraft?: ILeadSale;
  createGalleryLeadId?: string;
  manageGalleryId?: string;
  birth?: IBirth;
  birthLeadId?: string;
};

type ModalActions = {
  handleOpenLeadModal: (lead?: ILead) => void;
  handleOpenEventModal: (eventModalProps?: IEventModalProps) => void;
  handleOpenTodoModal: (todoModalProps?: ITodoModalProps) => void;
  handleCloseModal: () => void;
  handleOpenConfirmationModal: (props: IConfirmationModalProps) => void;
  handleCloseConfirmationModal: () => void;
  handleOpenServiceModal: (service?: IService) => void;
  handleOpenServicePackageModal: (servicePackage?: IServicePackage) => void;
  handleOpenDeleteServiceConflictModal: (payload: {
    service: IService;
    packages: IConflictPackage[];
  }) => void;
  handleOpenRegisterSaleModal: (payload: IRegisterSaleModalProps) => void;
  handleOpenMessageTemplateModal: (messageTemplate?: IMessageTemplate) => void;
  handleOpenMessageComposerModal: (lead: ILead) => void;
  handleOpenEventChecklistModal: (eventChecklist?: IEventChecklist) => void;
  handleOpenDeactivateLeadModal: (lead: ILead) => void;
  handleOpenChangePasswordModal: () => void;
  handleOpenManageBadgesModal: () => void;
  handleOpenQuotaLimitModal: (payload: IQuotaLimitPayload) => void;
  handleOpenUpgradePromptModal: (payload: IUpgradePromptPayload) => void;
  handleOpenPlanUpgradeModal: (payload: IPlanUpgradePayload) => void;
  handleOpenPlanDowngradeBlockedModal: (
    payload: IPlanDowngradeBlockedPayload,
  ) => void;
  handleOpenCancelSubscriptionModal: (
    payload: ICancelSubscriptionPayload,
  ) => void;
  handleSetSelectedPlanIdForChange: (planId: string | undefined) => void;
  handleOpenInviteMemberModal: () => void;
  handleOpenCollectDocumentModal: (payload: ICollectDocumentPayload) => void;
  handleOpenContractorModal: (contractor?: IContractor) => void;
  handleOpenContractTemplateModal: (
    contractTemplate?: IContractTemplate,
  ) => void;
  handleOpenIssueContractModal: (leadId: string) => void;
  handleOpenSendForSignatureModal: (payload: ISendForSignaturePayload) => void;
  handleOpenCreateGalleryModal: (leadId: string) => void;
  handleOpenManageGalleryModal: (galleryId: string) => void;
  handleOpenBirthModal: (payload: IBirthModalProps) => void;
};

export interface IBirthModalProps {
  leadId: string;
  birth?: IBirth;
}

export interface ISendForSignaturePayload {
  leadId: string;
  contractId: string;
  signerName?: string;
  signerEmail?: string;
}

export interface IEventDraft {
  leadId?: string;
  statusId?: string;
  description?: string;
  locationId?: string | null;
}

interface IEventModalProps {
  event?: IEvent;
  eventStatusId?: string;
  leadId?: string;
  eventDraft?: IEventDraft;
}

interface IRegisterSaleModalProps {
  leadId: string;
  saleDraft?: ILeadSale;
}

export interface ITodoModalProps {
  todo?: ITodo;
  leadId?: string;
  eventId?: string;
}

export type ModalSlice = ModalStore & ModalActions;

type ModalSetter = Parameters<StoreSlice<ModalSlice>>[0];

function createOpenModalHandler<TPayload>(
  set: ModalSetter,
  modalType: ModalTypeEnum,
  applyPayload?: (state: ModalStore, payload: TPayload) => void,
) {
  return (payload: TPayload) =>
    set((prevState) => {
      prevState.modal.openModal = modalType;
      applyPayload?.(prevState.modal, payload);
    });
}

export const createModalSlice: StoreSlice<ModalSlice> = (set) => ({
  openModal: null,
  isConfirmationModalOpen: false,

  handleOpenLeadModal: createOpenModalHandler<ILead | undefined>(
    set,
    ModalTypeEnum.Lead,
    (state, lead) => {
      state.lead = lead;
    },
  ),

  handleOpenEventModal: createOpenModalHandler<IEventModalProps | undefined>(
    set,
    ModalTypeEnum.Event,
    (state, eventModalProps) => {
      const { event, eventStatusId, leadId, eventDraft } =
        eventModalProps || {};
      state.event = event;
      state.eventStatusId = eventStatusId;
      state.leadId = leadId;
      state.eventDraft = eventDraft;
    },
  ),

  handleOpenTodoModal: createOpenModalHandler<ITodoModalProps | undefined>(
    set,
    ModalTypeEnum.Todo,
    (state, todoModalProps) => {
      const { todo, leadId, eventId } = todoModalProps || {};
      state.todo = todo;
      state.todoLeadId = leadId;
      state.todoEventId = eventId;
    },
  ),

  handleCloseModal: () =>
    set((prevState) => {
      prevState.modal.openModal = null;
      prevState.modal.quotaLimit = undefined;
      prevState.modal.upgradePrompt = undefined;
      prevState.modal.planUpgrade = undefined;
      prevState.modal.planDowngradeBlocked = undefined;
      prevState.modal.cancelSubscription = undefined;
      prevState.modal.selectedPlanIdForChange = undefined;
      prevState.modal.collectDocument = undefined;
      prevState.modal.eventDraft = undefined;
      prevState.modal.saleDraft = undefined;
      prevState.modal.messageTemplate = undefined;
      prevState.modal.messageComposerLead = undefined;
      prevState.modal.eventChecklist = undefined;
      prevState.modal.deactivateLead = undefined;
      prevState.modal.sendForSignature = undefined;
      prevState.modal.createGalleryLeadId = undefined;
      prevState.modal.manageGalleryId = undefined;
      prevState.modal.birth = undefined;
      prevState.modal.birthLeadId = undefined;
    }),

  handleOpenConfirmationModal: (confirmationModalProps) =>
    set((prevState) => {
      prevState.modal.isConfirmationModalOpen = true;
      prevState.modal.confirmationModalProps = confirmationModalProps;
    }),

  handleCloseConfirmationModal: () =>
    set((prevState) => {
      prevState.modal.isConfirmationModalOpen = false;
      prevState.modal.confirmationModalProps = undefined;
    }),

  handleOpenServiceModal: createOpenModalHandler<IService | undefined>(
    set,
    ModalTypeEnum.Service,
    (state, service) => {
      state.service = service;
    },
  ),

  handleOpenServicePackageModal: createOpenModalHandler<
    IServicePackage | undefined
  >(set, ModalTypeEnum.ServicePackage, (state, servicePackage) => {
    state.servicePackage = servicePackage;
  }),

  handleOpenDeleteServiceConflictModal: createOpenModalHandler<{
    service: IService;
    packages: IConflictPackage[];
  }>(set, ModalTypeEnum.DeleteServiceConflict, (state, payload) => {
    state.deleteServiceConflict = payload;
  }),

  handleOpenRegisterSaleModal: createOpenModalHandler<IRegisterSaleModalProps>(
    set,
    ModalTypeEnum.RegisterSale,
    (state, { leadId, saleDraft }) => {
      state.registerSaleLeadId = leadId;
      state.saleDraft = saleDraft;
    },
  ),

  handleOpenMessageTemplateModal: createOpenModalHandler<
    IMessageTemplate | undefined
  >(set, ModalTypeEnum.MessageTemplate, (state, messageTemplate) => {
    state.messageTemplate = messageTemplate;
  }),

  handleOpenMessageComposerModal: createOpenModalHandler<ILead>(
    set,
    ModalTypeEnum.MessageComposer,
    (state, lead) => {
      state.messageComposerLead = lead;
    },
  ),

  handleOpenEventChecklistModal: createOpenModalHandler<
    IEventChecklist | undefined
  >(set, ModalTypeEnum.EventChecklist, (state, eventChecklist) => {
    state.eventChecklist = eventChecklist;
  }),

  handleOpenDeactivateLeadModal: createOpenModalHandler<ILead>(
    set,
    ModalTypeEnum.DeactivateLead,
    (state, lead) => {
      state.deactivateLead = lead;
    },
  ),

  handleOpenChangePasswordModal: createOpenModalHandler<void>(
    set,
    ModalTypeEnum.ChangePassword,
  ),

  handleOpenManageBadgesModal: createOpenModalHandler<void>(
    set,
    ModalTypeEnum.ManageBadges,
  ),

  handleOpenQuotaLimitModal: createOpenModalHandler<IQuotaLimitPayload>(
    set,
    ModalTypeEnum.QuotaLimit,
    (state, payload) => {
      state.quotaLimit = payload;
    },
  ),

  handleOpenUpgradePromptModal: createOpenModalHandler<IUpgradePromptPayload>(
    set,
    ModalTypeEnum.UpgradePrompt,
    (state, payload) => {
      state.upgradePrompt = payload;
    },
  ),

  handleOpenPlanUpgradeModal: createOpenModalHandler<IPlanUpgradePayload>(
    set,
    ModalTypeEnum.PlanUpgradeConfirm,
    (state, payload) => {
      state.planUpgrade = payload;
      state.selectedPlanIdForChange = payload.planId;
    },
  ),

  handleOpenPlanDowngradeBlockedModal:
    createOpenModalHandler<IPlanDowngradeBlockedPayload>(
      set,
      ModalTypeEnum.PlanDowngradeBlocked,
      (state, payload) => {
        state.planDowngradeBlocked = payload;
      },
    ),

  handleOpenCancelSubscriptionModal:
    createOpenModalHandler<ICancelSubscriptionPayload>(
      set,
      ModalTypeEnum.CancelSubscription,
      (state, payload) => {
        state.cancelSubscription = payload;
      },
    ),

  handleSetSelectedPlanIdForChange: (planId) =>
    set((prevState) => {
      prevState.modal.selectedPlanIdForChange = planId;
    }),

  handleOpenInviteMemberModal: createOpenModalHandler<void>(
    set,
    ModalTypeEnum.InviteMember,
  ),

  handleOpenCollectDocumentModal:
    createOpenModalHandler<ICollectDocumentPayload>(
      set,
      ModalTypeEnum.CollectDocument,
      (state, payload) => {
        state.collectDocument = payload;
      },
    ),

  handleOpenContractorModal: createOpenModalHandler<IContractor | undefined>(
    set,
    ModalTypeEnum.Contractor,
    (state, contractor) => {
      state.contractor = contractor;
    },
  ),

  handleOpenContractTemplateModal: createOpenModalHandler<
    IContractTemplate | undefined
  >(set, ModalTypeEnum.ContractTemplate, (state, contractTemplate) => {
    state.contractTemplate = contractTemplate;
  }),

  handleOpenIssueContractModal: createOpenModalHandler<string>(
    set,
    ModalTypeEnum.IssueContract,
    (state, leadId) => {
      state.issueContractLeadId = leadId;
    },
  ),

  handleOpenSendForSignatureModal:
    createOpenModalHandler<ISendForSignaturePayload>(
      set,
      ModalTypeEnum.SendForSignature,
      (state, payload) => {
        state.sendForSignature = payload;
      },
    ),

  handleOpenCreateGalleryModal: createOpenModalHandler<string>(
    set,
    ModalTypeEnum.CreateGallery,
    (state, leadId) => {
      state.createGalleryLeadId = leadId;
    },
  ),

  handleOpenManageGalleryModal: createOpenModalHandler<string>(
    set,
    ModalTypeEnum.ManageGallery,
    (state, galleryId) => {
      state.manageGalleryId = galleryId;
    },
  ),

  handleOpenBirthModal: createOpenModalHandler<IBirthModalProps>(
    set,
    ModalTypeEnum.Birth,
    (state, { leadId, birth }) => {
      state.birthLeadId = leadId;
      state.birth = birth;
    },
  ),
});
